import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, BookBookmark } from "@phosphor-icons/react";
import { AdminHeader, Field, inputCls, PrimaryButton, GhostButton, StatusBadge, Dot } from "@/components/admin/parts";
import { adminApi, getErrorMessage } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const BLANK = {
  title: "", category: "", applies_to: "", technical_information: "",
  ai_enabled: true, customer_enabled: false, status: "approved",
};

export default function KnowledgeBase() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // object or null
  const [open, setOpen] = useState(false);

  const load = () => { setLoading(true); adminApi.get("/admin/knowledge").then(({ data }) => setRows(data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ ...BLANK }); setOpen(true); };
  const openEdit = (r) => { setEditing({ ...r, applies_to: (r.applies_to || []).join(", ") }); setOpen(true); };

  const save = async () => {
    const payload = {
      title: editing.title,
      category: editing.category,
      applies_to: (editing.applies_to || "").split(",").map((s) => s.trim()).filter(Boolean),
      technical_information: editing.technical_information,
      ai_enabled: editing.ai_enabled,
      customer_enabled: editing.customer_enabled,
      status: editing.status,
      source: editing.source || "manual",
    };
    if (!payload.title || !payload.technical_information) { toast.error("Title and technical information are required"); return; }
    try {
      if (editing.id) await adminApi.put(`/admin/knowledge/${editing.id}`, payload);
      else await adminApi.post("/admin/knowledge", payload);
      toast.success("Saved");
      setOpen(false); load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    await adminApi.delete(`/admin/knowledge/${r.id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div>
      <AdminHeader
        title="Knowledge Entries"
        subtitle="Capture field knowledge Nick can use, without needing a formal PDF"
        actions={<PrimaryButton onClick={openNew} data-testid="knowledge-new"><Plus size={16} /> New Entry</PrimaryButton>}
      />
      <div className="p-8">
        {loading ? <div className="text-neutral-600">Loading...</div> : rows.length === 0 ? (
          <div className="text-neutral-600 border border-dashed border-neutral-800 rounded-xl py-16 text-center">No knowledge entries yet.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {rows.map((r) => (
              <div key={r.id} data-testid="knowledge-card" className="bg-[#0F0F0F] border border-neutral-800 rounded-xl p-5">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <BookBookmark size={18} className="text-[#73bf44] shrink-0" weight="duotone" />
                    <h3 className="font-heading font-semibold text-white truncate">{r.title}</h3>
                  </div>
                  <StatusBadge status={r.status} />
                </div>
                {r.category && <div className="text-xs text-neutral-500 mb-2">{r.category}</div>}
                <p className="text-sm text-neutral-400 line-clamp-3 mb-3">{r.technical_information}</p>
                <div className="flex items-center justify-between">
                  <div className="flex gap-4">
                    <Dot on={r.ai_enabled} label="AI" />
                    <Dot on={r.customer_enabled} label="Customer" />
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(r)} data-testid="knowledge-edit" className="p-2 rounded-lg text-neutral-400 hover:text-white hover:bg-white/[0.05]"><PencilSimple size={16} /></button>
                    <button onClick={() => del(r)} data-testid="knowledge-delete" className="p-2 rounded-lg text-neutral-400 hover:text-red-400 hover:bg-red-500/10"><Trash size={16} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F0F0F] border-neutral-800 text-neutral-200 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-white">{editing?.id ? "Edit" : "New"} Knowledge Entry</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <Field label="Title"><input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={inputCls} data-testid="knowledge-title" placeholder="e.g. PM335 Solar Input Configuration" /></Field>
              <Field label="Category"><input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className={inputCls} placeholder="Electrical > Solar > PM335" /></Field>
              <Field label="Applies to (comma separated)"><input value={editing.applies_to} onChange={(e) => setEditing({ ...editing, applies_to: e.target.value })} className={inputCls} placeholder="Adventure, Grenade, Storm" /></Field>
              <Field label="Technical information"><textarea value={editing.technical_information} onChange={(e) => setEditing({ ...editing, technical_information: e.target.value })} rows={5} className={inputCls} data-testid="knowledge-info" placeholder="The PM335 supports a maximum solar input of..." /></Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Status">
                  <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className={inputCls} data-testid="knowledge-status">
                    <option value="draft">Draft</option><option value="approved">Approved</option><option value="archived">Archived</option>
                  </select>
                </Field>
                <label className="flex items-center gap-2 mt-7 cursor-pointer">
                  <input type="checkbox" checked={editing.ai_enabled} onChange={(e) => setEditing({ ...editing, ai_enabled: e.target.checked })} className="accent-[#73bf44] w-4 h-4" data-testid="knowledge-ai" />
                  <span className="text-sm">AI access</span>
                </label>
                <label className="flex items-center gap-2 mt-7 cursor-pointer">
                  <input type="checkbox" checked={editing.customer_enabled} onChange={(e) => setEditing({ ...editing, customer_enabled: e.target.checked })} className="accent-[#73bf44] w-4 h-4" data-testid="knowledge-customer" />
                  <span className="text-sm">Customer</span>
                </label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <GhostButton onClick={() => setOpen(false)}>Cancel</GhostButton>
                <PrimaryButton onClick={save} data-testid="knowledge-save">Save Entry</PrimaryButton>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
